import './style.css';
import { cosmicCalendarDate, formatCosmicDateJa, formatCosmicDateEn, logTimePosition, UNIVERSE_AGE_YEARS } from './engines/cosmichistory';
import { EARLY_UNIVERSE_EVENTS, CALENDAR_EVENTS, type CalendarEvent, type EarlyUniverseEvent } from './data/cosmicHistoryEvents';

// ============================================================
// 言語切り替え
// ============================================================
const langBtn = document.getElementById('langToggle') as HTMLButtonElement;
let currentLang: 'ja' | 'en' = 'ja';

function syncSliderFill(input: HTMLInputElement) {
  const min = parseFloat(input.min || '0');
  const max = parseFloat(input.max || '100');
  const val = parseFloat(input.value);
  const percent = ((val - min) / (max - min)) * 100;
  input.style.setProperty('--range-progress', `${percent}%`);
}

function applyLang(lang: 'ja' | 'en') {
  currentLang = lang;
  document.documentElement.lang = lang;
  document.querySelectorAll<HTMLElement>('[data-ja]').forEach((el) => {
    el.textContent = lang === 'ja' ? el.getAttribute('data-ja') : el.getAttribute('data-en');
  });
  langBtn.querySelectorAll<HTMLElement>('.opt').forEach((opt) => {
    opt.classList.toggle('on', opt.getAttribute('data-lang-opt') === lang);
  });
  const footerMessageJa = document.getElementById('footerMessageJa');
  const footerMessageEn = document.getElementById('footerMessageEn');
  if (footerMessageJa && footerMessageEn) {
    (footerMessageJa as HTMLElement).style.display = lang === 'ja' ? 'block' : 'none';
    (footerMessageEn as HTMLElement).style.display = lang === 'en' ? 'block' : 'none';
  }
  if (selectedCalendarEvent) showCalendarEventDetail(selectedCalendarEvent);
  else if (selectedEarlyEvent) showEarlyEventDetail(selectedEarlyEvent);
  renderLogTimeline();
  renderYearDots();
  renderDec31Zoom();
  if (yearScrubber) setYearPosition(parseFloat(yearScrubber.value) / 1000, false);
}
langBtn.addEventListener('click', () => applyLang(currentLang === 'ja' ? 'en' : 'ja'));

// ============================================================
// オンボーディング
// ============================================================
const ONBOARDING_KEY = 'cosmos-simulator:onboarding-seen-history';
const onboarding = document.getElementById('onboarding') as HTMLElement;
const onboardingDismiss = document.getElementById('onboardingDismiss') as HTMLButtonElement;
const helpBtn = document.getElementById('helpBtn') as HTMLButtonElement;
onboardingDismiss.addEventListener('click', () => {
  onboarding.classList.add('hidden');
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* noop */
  }
});
helpBtn.addEventListener('click', () => onboarding.classList.remove('hidden'));
try {
  if (localStorage.getItem(ONBOARDING_KEY) === '1') onboarding.classList.add('hidden');
} catch {
  /* noop */
}

// ============================================================
// DOM参照
// ============================================================
const eventName = document.getElementById('eventName') as HTMLElement;
const eventDesc = document.getElementById('eventDesc') as HTMLElement;
const eventRealAge = document.getElementById('eventRealAge') as HTMLElement;
const eventCalDate = document.getElementById('eventCalDate') as HTMLElement;
const validationNote = document.getElementById('validationNote') as HTMLElement;

let selectedCalendarEvent: CalendarEvent | null = null;
let selectedEarlyEvent: EarlyUniverseEvent | null = null;

// 億年前・万年前の日本語表記を正しく整形するヘルパー
function formatYearsAgoJaClean(yearsAgo: number): string {
  if (yearsAgo <= 0.001) return '現在';
  if (yearsAgo >= 1e8) {
    const oku = yearsAgo / 1e8;
    return `約${oku.toFixed(2)}億年前`;
  }
  if (yearsAgo >= 1e4) {
    const man = yearsAgo / 1e4;
    return `約${man.toFixed(1)}万年前`;
  }
  return `約${Math.round(yearsAgo).toLocaleString()}年前`;
}
function formatYearsAgoEnClean(yearsAgo: number): string {
  if (yearsAgo <= 0.001) return 'Now';
  if (yearsAgo >= 1e9) return `~${(yearsAgo / 1e9).toFixed(2)} billion years ago`;
  if (yearsAgo >= 1e6) return `~${(yearsAgo / 1e6).toFixed(1)} million years ago`;
  return `~${Math.round(yearsAgo).toLocaleString()} years ago`;
}

function showCalendarEventDetail(ev: CalendarEvent) {
  selectedCalendarEvent = ev;
  selectedEarlyEvent = null;
  eventName.textContent = currentLang === 'ja' ? ev.nameJa : ev.nameEn;
  eventDesc.textContent = currentLang === 'ja' ? ev.descJa : ev.descEn;
  eventRealAge.textContent = currentLang === 'ja' ? formatYearsAgoJaClean(ev.yearsAgo) : formatYearsAgoEnClean(ev.yearsAgo);
  const d = cosmicCalendarDate(Math.max(ev.yearsAgo, 0.0001));
  eventCalDate.textContent = currentLang === 'ja' ? formatCosmicDateJa(d) : formatCosmicDateEn(d);
}

function showEarlyEventDetail(ev: EarlyUniverseEvent) {
  selectedEarlyEvent = ev;
  selectedCalendarEvent = null;
  eventName.textContent = currentLang === 'ja' ? ev.nameJa : ev.nameEn;
  eventDesc.textContent = currentLang === 'ja' ? ev.descJa : ev.descEn;
  const years = ev.secondsAfterBigBang / (365.25 * 24 * 3600);
  const label =
    ev.secondsAfterBigBang < 3600
      ? currentLang === 'ja'
        ? `ビッグバンから約${ev.secondsAfterBigBang.toExponential(2)}秒後`
        : `~${ev.secondsAfterBigBang.toExponential(2)} s after the Big Bang`
      : currentLang === 'ja'
        ? `ビッグバンから約${years.toExponential(2)}年後`
        : `~${years.toExponential(2)} years after the Big Bang`;
  eventRealAge.textContent = label;
  eventCalDate.textContent = '—';
}

// ============================================================
// 早期宇宙タイムライン（左パネル）
// ============================================================
const logTimeline = document.getElementById('logTimeline') as HTMLElement;

function renderLogTimeline() {
  logTimeline.innerHTML = '';
  // 【重要】ここは必ず固定の基準値を使うこと。もし現在のコンテナの実際の高さ
  // (getBoundingClientRect().height)を基準にしてしまうと、以前の呼び出しで
  // 自分自身が広げた高さを次の計算のベースにしてしまい、resizeイベントの
  // たびに際限なく縦に伸び続ける無限フィードバックループになる
  // (実際にiPad Safariでアドレスバーの出し入れによるresizeで発生した不具合)。
  const BASE_HEIGHT = 340;
  logTimeline.style.height = `${BASE_HEIGHT}px`; // 再計算のたびに基準値へ一度リセットする
  const maxExp = Math.log10(1e9 * 365.25 * 24 * 3600); // 10億年を秒に

  // 実際の対数位置を計算したうえで、ラベルが重ならないよう最小間隔を確保する
  // (視認性のための調整。正確な数値は各行のテキストラベルに表示される)
  const MIN_GAP_PX = 40;
  const rawPositions = EARLY_UNIVERSE_EVENTS.map((ev) => logTimePosition(ev.secondsAfterBigBang, -44, maxExp) * BASE_HEIGHT);
  const adjustedPositions: number[] = [];
  rawPositions.forEach((pos, i) => {
    if (i === 0) {
      adjustedPositions.push(pos);
    } else {
      adjustedPositions.push(Math.max(pos, adjustedPositions[i - 1] + MIN_GAP_PX));
    }
  });

  EARLY_UNIVERSE_EVENTS.forEach((ev, i) => {
    const row = document.createElement('div');
    row.className = 'log-event';
    row.style.top = `${adjustedPositions[i]}px`;

    const dot = document.createElement('div');
    dot.className = 'dot';

    const lbl = document.createElement('div');
    lbl.className = 'lbl';
    const timeStr =
      ev.secondsAfterBigBang < 3600
        ? `${ev.secondsAfterBigBang.toExponential(1)} s`
        : `${(ev.secondsAfterBigBang / (365.25 * 24 * 3600)).toExponential(1)} yr`;
    lbl.innerHTML = `<span class="t">t ≈ ${timeStr}</span>${currentLang === 'ja' ? ev.nameJa : ev.nameEn}`;

    row.appendChild(dot);
    row.appendChild(lbl);
    row.addEventListener('click', () => showEarlyEventDetail(ev));
    logTimeline.appendChild(row);
  });

  // 最後の行がコンテナからはみ出す場合だけ、固定基準値からの相対値で高さを広げる
  const lastPos = adjustedPositions[adjustedPositions.length - 1];
  if (lastPos + MIN_GAP_PX > BASE_HEIGHT) {
    logTimeline.style.height = `${lastPos + MIN_GAP_PX}px`;
  }
}

// ============================================================
// 宇宙暦 年間ストリップ（中央パネル）
// ============================================================
const yearMonths = document.getElementById('yearMonths') as HTMLElement;
const yearDots = document.getElementById('yearDots') as HTMLElement;
const MONTH_LABELS_JA = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
const MONTH_LABELS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function renderYearMonths() {
  yearMonths.innerHTML = '';
  for (let i = 0; i < 12; i++) {
    const span = document.createElement('span');
    span.textContent = currentLang === 'ja' ? MONTH_LABELS_JA[i] : MONTH_LABELS_EN[i];
    yearMonths.appendChild(span);
  }
}

interface DotEntry {
  el: HTMLElement;
  fraction: number;
  ev: CalendarEvent;
}
let yearDotEntries: DotEntry[] = [];

function renderYearDots() {
  yearDots.innerHTML = '';
  yearDotEntries = [];
  const rowCount = 4;
  CALENDAR_EVENTS.forEach((ev, i) => {
    if (ev.id === 'now') return; // "現在" は暦の定義そのものなので点は打たない
    const d = cosmicCalendarDate(Math.max(ev.yearsAgo, 0.0001));
    const xFrac = d.secondsFromStart / (365 * 24 * 3600);
    const dot = document.createElement('div');
    dot.className = 'year-dot' + (ev.category === 'human' ? ' human' : '');
    dot.style.left = `${xFrac * 100}%`;
    dot.style.top = `${((i % rowCount) / (rowCount - 1)) * 80 + 10}%`;
    dot.title = currentLang === 'ja' ? ev.nameJa : ev.nameEn;
    dot.addEventListener('click', () => showCalendarEventDetail(ev));
    yearDots.appendChild(dot);
    yearDotEntries.push({ el: dot, fraction: xFrac, ev });
  });
}

// ============================================================
// 大晦日 最後の15分ズーム（下部）
// ============================================================
const dec31Dots = document.getElementById('dec31Dots') as HTMLElement;
const WINDOW_SECONDS = 15 * 60; // 最後の15分

let dec31DotEntries: DotEntry[] = [];

// この15分の枠に入る出来事は常に決まっているため、コンテナ幅の実測に依存しない
// 固定の段番号を割り当てる(動的なビンパッキングだとレイアウトのタイミングで不安定になるため)
const DEC31_ROW_BY_ID: Record<string, number> = {
  humans: 0,
  agriculture: 0,
  writing: 1,
  now: 2,
};

// このズームパネルは表示幅が狭いため、正式名称ではなく短縮表記を使う
// (フルネーム・説明は右パネルの詳細に出るのでここでは省略してよい)
const DEC31_SHORT_LABEL: Record<string, { ja: string; en: string }> = {
  humans: { ja: '人類誕生', en: 'Humans' },
  agriculture: { ja: '農耕開始', en: 'Agriculture' },
  writing: { ja: '文字の発明', en: 'Writing' },
  now: { ja: '現在', en: 'Now' },
};

function renderDec31Zoom() {
  dec31Dots.innerHTML = '';
  dec31DotEntries = [];

  const zoomEvents = CALENDAR_EVENTS.filter((ev) => {
    const d = cosmicCalendarDate(Math.max(ev.yearsAgo, 0.0001));
    return d.secondsUntilNow <= WINDOW_SECONDS;
  });

  const computed = zoomEvents.map((ev) => {
    const d = cosmicCalendarDate(Math.max(ev.yearsAgo, 0.0001));
    const fraction = ev.id === 'now' ? 1 : 1 - d.secondsUntilNow / WINDOW_SECONDS;
    return { ev, fraction: Math.max(0, Math.min(1, fraction)) };
  });
  computed.sort((a, b) => a.fraction - b.fraction);

  computed.forEach(({ ev, fraction }) => {
    const dotWrap = document.createElement('div');
    const row = DEC31_ROW_BY_ID[ev.id] ?? 0;
    const rowClass = row > 0 ? ` row-${row}` : '';
    const edgeClass = fraction > 0.93 ? ' anchor-right' : fraction < 0.05 ? ' anchor-left' : '';
    dotWrap.className = 'dec31-dot' + rowClass + edgeClass;
    dotWrap.style.left = `${fraction * 100}%`;

    const pt = document.createElement('div');
    pt.className = 'pt';
    const tag = document.createElement('div');
    tag.className = 'tag';
    const shortLabel = DEC31_SHORT_LABEL[ev.id];
    tag.textContent = shortLabel ? (currentLang === 'ja' ? shortLabel.ja : shortLabel.en) : currentLang === 'ja' ? ev.nameJa : ev.nameEn;

    dotWrap.appendChild(pt);
    dotWrap.appendChild(tag);
    dotWrap.addEventListener('click', () => showCalendarEventDetail(ev));
    dec31Dots.appendChild(dotWrap);
    dec31DotEntries.push({ el: dotWrap, fraction, ev });
  });
}

// ============================================================
// シーカー・再生（宇宙暦 年間ストリップ + 大晦日ズームは自動連動）
// ============================================================
const yearPlayhead = document.getElementById('yearPlayhead') as HTMLElement;
const yearScrubber = document.getElementById('yearScrubber') as HTMLInputElement;
const yearScrubLabel = document.getElementById('yearScrubLabel') as HTMLElement;
const yearPlayBtn = document.getElementById('yearPlayBtn') as HTMLButtonElement;
const dec31Playhead = document.getElementById('dec31Playhead') as HTMLElement;
const dec31ScrubLabel = document.getElementById('dec31ScrubLabel') as HTMLElement;

const NEAR_THRESHOLD = 0.004; // 全体に対する割合。この範囲内のドットを「近い」とみなす
// (以前は0.012だったが、再生を大幅に減速させた区間では0.012が2秒分もの
// 時間差に相当し、背景画像の切り替わりよりだいぶ早くラベルだけ切り替わって
// しまう問題があったため、より狭い値に調整した)
const DEC31_WINDOW_START_SECONDS = 23 * 3600 + 45 * 60; // 23:45:00

// 大晦日ズームパネルは、メインのシーカーが最後の15分に入っているときだけ連動して動く
function updateDec31FromYearFraction(yearFraction: number) {
  const yearsAgo = UNIVERSE_AGE_YEARS * (1 - yearFraction);
  const d = cosmicCalendarDate(Math.max(yearsAgo, 0.0001));

  if (d.secondsUntilNow > WINDOW_SECONDS) {
    // まだ最後の15分に入っていない → 再生ヘッドは開始位置で待機、薄く表示
    dec31Playhead.style.left = '0%';
    dec31Playhead.style.opacity = '0.25';
    dec31ScrubLabel.textContent = '23:45:00';
    dec31DotEntries.forEach((entry) => entry.el.classList.remove('near-playhead'));
    return;
  }

  dec31Playhead.style.opacity = '1';
  const dec31Fraction = 1 - d.secondsUntilNow / WINDOW_SECONDS;
  dec31Playhead.style.left = `${dec31Fraction * 100}%`;

  const totalSeconds = DEC31_WINDOW_START_SECONDS + dec31Fraction * WINDOW_SECONDS;
  const hh = Math.floor(totalSeconds / 3600);
  const hhStr = hh >= 24 ? '24' : String(hh).padStart(2, '0');
  const mm = hh >= 24 ? '00' : String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const ss = hh >= 24 ? '00' : String(Math.floor(totalSeconds % 60)).padStart(2, '0');
  dec31ScrubLabel.textContent = `${hhStr}:${mm}:${ss}`;

  let nearestEntry: DotEntry | null = null;
  let nearestDist = Infinity;
  dec31DotEntries.forEach((entry) => {
    const dist = Math.abs(entry.fraction - dec31Fraction);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearestEntry = entry;
    }
  });
  dec31DotEntries.forEach((entry) => {
    entry.el.classList.toggle('near-playhead', entry === nearestEntry && nearestDist < NEAR_THRESHOLD * 1.5);
  });
}

// ============================================================
// 時代ごとの背景演出(eraBackdrop)
//
// 【この演出の性質】実際の138億年をそのまま比例配分すると、銀河形成〜現代
// までの後半が背景としてほぼ一瞬しか見えなくなってしまう(宇宙暦の1年間の
// 表示と同じ「圧縮の副作用」)。そのため背景の切り替えタイミングは、時系列の
// 順序だけは正確に保ちつつ、UI上で each 時代を十分な幅で操作・鑑賞できるよう
// 意図的に配分している(演出上の簡略化であり、宇宙暦本体の日時計算とは別物)。
// ============================================================
const ERA_ZONES: { id: string; from: number; to: number }[] = [
  { id: 'bg-bigbang', from: 0, to: 0.001 },
  { id: 'bg-darkages', from: 0.001, to: 0.014 },
  { id: 'bg-firststar', from: 0.014, to: 0.022 },
  { id: 'bg-galaxies', from: 0.022, to: 0.6667 }, // 実際の太陽系形成(46億年前, fraction≈0.6667)まで
  { id: 'bg-solarsystem', from: 0.6667, to: 0.72 },
  { id: 'bg-earth', from: 0.72, to: 0.99995 }, // 地球形成〜生命の進化(霊長類まで含む)。人類誕生(69万年前)の直前まで
  { id: 'bg-modern', from: 0.99995, to: 1.001 }, // 人類誕生(fraction≈0.999978, 約69万年前以降)
];
let currentEraId = '';
let eraBackdropReady = false; // 初回のビッグバンだけ、明示的にフェードインさせるまで表示を保留する
function updateEraBackground(fraction: number) {
  const zone = ERA_ZONES.find((z) => fraction >= z.from && fraction < z.to) ?? ERA_ZONES[ERA_ZONES.length - 1];
  if (zone.id === currentEraId) return;
  currentEraId = zone.id;
  if (!eraBackdropReady) return; // 初回はまだ何も表示しない(下のsetTimeoutでフェードインする)
  document.querySelectorAll<HTMLElement>('#eraBackdrop .bg-layer').forEach((el) => {
    el.classList.toggle('show', el.id === zone.id);
  });
}

function setYearPosition(fraction: number, updateDetail = true) {
  yearPlayhead.style.left = `${fraction * 100}%`;
  syncSliderFill(yearScrubber);
  const yearsAgo = UNIVERSE_AGE_YEARS * (1 - fraction);
  const d = cosmicCalendarDate(Math.max(yearsAgo, 0.0001));
  yearScrubLabel.textContent = currentLang === 'ja' ? formatCosmicDateJa(d) : formatCosmicDateEn(d);
  updateEraBackground(fraction);

  let nearest: DotEntry | null = null;
  let nearestDist = Infinity;
  yearDotEntries.forEach((entry) => {
    const dist = Math.abs(entry.fraction - fraction);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = entry;
    }
  });
  yearDotEntries.forEach((entry) => {
    entry.el.classList.toggle('near-playhead', entry === nearest && nearestDist < NEAR_THRESHOLD);
  });
  if (updateDetail && nearest && nearestDist < NEAR_THRESHOLD * 1.5) {
    showCalendarEventDetail((nearest as DotEntry).ev);
  }

  updateDec31FromYearFraction(fraction);
}

let yearPlaying = false;
let yearRAF: number | null = null;
let yearStartTime: number | null = null;

function stopYearPlayback() {
  yearPlaying = false;
  yearPlayBtn.textContent = '▶';
  yearPlayBtn.classList.remove('playing');
  if (yearRAF !== null) cancelAnimationFrame(yearRAF);
}

// 再生時の「間(ま)」を演出として直接コントロールするためのウェイポイント。
// 経過秒数(累積) → その時点でのfraction値、を並べ、区間ごとに滑らかに補間する。
// 単一の数式で全体を動かす方式だと、各時代の実際の表示時間は計算結果まかせに
// なってしまい、「爆発は一瞬の勢い、静寂を挟んで、最初の光がほわっと灯る」
// といった意図的なリズムを作るのが難しかったため、この方式に変更した。
const PLAYBACK_WAYPOINTS: { t: number; f: number }[] = [
  { t: 0, f: 0 }, // ビッグバン開始
  { t: 0.6, f: 0.001 }, // ビッグバン: 一瞬の勢いで駆け抜ける
  { t: 2.1, f: 0.014 }, // 暗黒時代: 静寂
  { t: 4.6, f: 0.022 }, // 最初の恒星: ほわっとゆっくり灯り、しばらく留まる
  { t: 8.6, f: 0.55 }, // 銀河形成
  { t: 10.1, f: 0.64 }, // 太陽系誕生に向けて減速し始める
  { t: 12.0, f: 0.666667 }, // 太陽系形成(実際の時期, 46億年前)
  { t: 13.3, f: 0.71 }, // 地球形成の直後まで、少し長めに留まる
  { t: 16.3, f: 0.99726 }, // 大晦日突入(宇宙暦12月31日 0:00)
  { t: 18.0, f: 0.999978 }, // 人類誕生
  { t: 20.0, f: 1.0 }, // 現在(この区間だけで農耕・文字の発明・現在までを含む)
];
const PLAYBACK_DURATION_S = PLAYBACK_WAYPOINTS[PLAYBACK_WAYPOINTS.length - 1].t;

// PCHIP風(単調3次エルミート補間)の接線を計算する。
// 単純にsmoothstepで区間ごとに補間すると、すべてのウェイポイントの前後で
// 速度がいったんゼロまで落ちてから再加速するため、7区間=6箇所で
// 「一瞬止まる」ようなカクカクした動きになってしまっていた。
// ここでは各ウェイポイントでの速度(接線)を前後の区間の傾きから求め、
// 最初と最後だけを静止状態(接線0)にすることで、その間は速度が
// 途切れないなめらかな動きにする。傾きの符号が変わる/ゼロを含む場合は
// 接線を0にし、そうでなければ調和平均を使うことで単調性(逆走しないこと)
// を保証する(PCHIP: Piecewise Cubic Hermite Interpolating Polynomial の手法)。
function computeMonotoneTangents(points: { t: number; f: number }[]): number[] {
  const n = points.length;
  const tangents = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const dPrev = (points[i].f - points[i - 1].f) / (points[i].t - points[i - 1].t);
    const dNext = (points[i + 1].f - points[i].f) / (points[i + 1].t - points[i].t);
    tangents[i] = dPrev * dNext <= 0 ? 0 : (2 * dPrev * dNext) / (dPrev + dNext);
  }
  tangents[0] = 0; // 静止状態からなめらかに動き出す
  tangents[n - 1] = 0; // 最後はなめらかに静止する
  return tangents;
}
const PLAYBACK_TANGENTS = computeMonotoneTangents(PLAYBACK_WAYPOINTS);

function fractionAtElapsedSeconds(elapsedS: number): number {
  if (elapsedS <= 0) return 0;
  if (elapsedS >= PLAYBACK_DURATION_S) return 1;
  for (let i = 0; i < PLAYBACK_WAYPOINTS.length - 1; i++) {
    const a = PLAYBACK_WAYPOINTS[i];
    const b = PLAYBACK_WAYPOINTS[i + 1];
    if (elapsedS >= a.t && elapsedS <= b.t) {
      const dt = b.t - a.t;
      const s = (elapsedS - a.t) / dt;
      const s2 = s * s;
      const s3 = s2 * s;
      const h00 = 2 * s3 - 3 * s2 + 1;
      const h10 = s3 - 2 * s2 + s;
      const h01 = -2 * s3 + 3 * s2;
      const h11 = s3 - s2;
      const m0 = PLAYBACK_TANGENTS[i] * dt;
      const m1 = PLAYBACK_TANGENTS[i + 1] * dt;
      return h00 * a.f + h10 * m0 + h01 * b.f + h11 * m1;
    }
  }
  return 1;
}

function playYear() {
  yearPlaying = true;
  yearPlayBtn.textContent = '⏸';
  yearPlayBtn.classList.add('playing');
  yearStartTime = null;

  function step(ts: number) {
    if (!yearPlaying) return;
    if (yearStartTime === null) yearStartTime = ts;
    const elapsedS = (ts - yearStartTime) / 1000;
    const eased = fractionAtElapsedSeconds(elapsedS);
    yearScrubber.value = String(eased * 1000);
    setYearPosition(eased);
    if (elapsedS < PLAYBACK_DURATION_S) {
      yearRAF = requestAnimationFrame(step);
    } else {
      stopYearPlayback();
    }
  }
  yearRAF = requestAnimationFrame(step);
}

yearScrubber.addEventListener('input', () => {
  stopYearPlayback();
  setYearPosition(parseFloat(yearScrubber.value) / 1000);
});
yearPlayBtn.addEventListener('click', () => {
  if (yearPlaying) {
    stopYearPlayback();
  } else {
    yearScrubber.value = '0';
    playYear();
  }
});

// ============================================================
// 初期化
// ============================================================

// 銀河形成の背景: 黄金角による散布(フェルミページの銀河マップと同じ手法)
function scatterGalaxyBackdrop() {
  const container = document.getElementById('bg-galaxies');
  if (!container || container.childElementCount > 0) return;
  const rect = container.getBoundingClientRect();
  const cx = rect.width / 2 || window.innerWidth / 2;
  const cy = rect.height / 2 || window.innerHeight / 2;
  const maxR = Math.min(cx, cy) - 20;
  for (let i = 0; i < 140; i++) {
    const angle = (i * 137.5077) % 360;
    const radiusFrac = Math.sqrt(((i * 9973) % 1000) / 1000);
    const r = radiusFrac * maxR;
    const x = cx + r * Math.cos((angle * Math.PI) / 180);
    const y = cy + r * Math.sin((angle * Math.PI) / 180);
    const size = 3 + ((i * 37) % 5);
    const dot = document.createElement('div');
    dot.className = 'gal-dot';
    dot.style.left = `${x}px`;
    dot.style.top = `${y}px`;
    dot.style.width = `${size}px`;
    dot.style.height = `${size}px`;
    dot.style.opacity = String(0.4 + ((i * 13) % 60) / 100);
    container.appendChild(dot);
  }
}
scatterGalaxyBackdrop();

// 現代の背景: 淡い星空
function scatterModernStars() {
  const container = document.getElementById('bg-modern');
  if (!container || container.querySelector('.star-dot')) return;
  for (let i = 0; i < 70; i++) {
    const dot = document.createElement('div');
    dot.className = 'star-dot';
    dot.style.left = `${Math.random() * 100}%`;
    dot.style.top = `${Math.random() * 100}%`;
    container.appendChild(dot);
  }
}
scatterModernStars();

function renderAll() {
  renderLogTimeline();
  renderYearMonths();
  renderYearDots();
  renderDec31Zoom();
  setYearPosition(parseFloat(yearScrubber.value) / 1000, false);
}
renderAll();
window.addEventListener('resize', renderAll);

showCalendarEventDetail(CALENDAR_EVENTS.find((e) => e.id === 'bigbang')!);
validationNote.textContent =
  currentLang === 'ja'
    ? '文字の発明(5000年前) ≈ 大晦日の最後の11.4秒(定説「約10〜14秒」と一致)'
    : "Writing invented (5,000 yrs ago) ≈ 11.4s before midnight (matches the commonly cited '~10-14 seconds')";

// ============================================================
// 常設ティッカー:「私たちは宇宙史のどこにいるか」
// 宇宙暦の「今」は定義上つねに12月31日24:00で固定なので、
// 秒未満の部分だけ実時間の経過に合わせて演出的に流し続ける
// (科学的な精度の主張ではなく、「時間は止まらず流れ続けている」ことを
// 感じてもらうための演出であることに注意)
// ============================================================
const nowBadgeValue = document.getElementById('nowBadgeValue') as HTMLElement;
function tickNowBadge() {
  const frac = (performance.now() % 1000) / 1000; // 0.00〜0.99 を演出的にループさせる
  const centis = Math.floor(frac * 100);
  nowBadgeValue.textContent = `${currentLang === 'ja' ? '12月31日' : 'Dec 31'} 23:59:59.${String(centis).padStart(2, '0')}`;
  requestAnimationFrame(tickNowBadge);
}
tickNowBadge();

// ページローダーを隠す
const pageLoader = document.getElementById('pageLoader');
if (pageLoader) {
  requestAnimationFrame(() => {
    pageLoader.classList.add('hide');
    setTimeout(() => pageLoader.remove(), 400);
  });
}

// 時代ごとの背景: ページローダーが消えたタイミングで、真っ暗な状態からビッグバンをフェードインさせる
setTimeout(() => {
  eraBackdropReady = true;
  currentEraId = ''; // 一度リセットして、確実に表示処理が走るようにする
  updateEraBackground(parseFloat(yearScrubber.value) / 1000);
}, 500);

// ============================================================
// フッターメッセージ（クリックで開閉）
// ============================================================
const dedicationFooter = document.getElementById('dedicationFooter');
const footerMessageOverlay = document.getElementById('footerMessageOverlay');
const footerMessageClose = document.getElementById('footerMessageClose');
if (dedicationFooter && footerMessageOverlay && footerMessageClose) {
  dedicationFooter.addEventListener('click', () => {
    footerMessageOverlay.classList.add('show');
  });
  footerMessageClose.addEventListener('click', () => {
    footerMessageOverlay.classList.remove('show');
  });
  footerMessageOverlay.addEventListener('click', (e) => {
    if (e.target === footerMessageOverlay) footerMessageOverlay.classList.remove('show');
  });
}
